import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Plus, X, ClipboardList, Send, PackageCheck, Ban, Loader2, Upload, Download, AlertCircle } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Purchase Orders — the ordering + receiving surface. Lifecycle:
//   draft → sent → partial → received     (or → cancelled)
// The list is filterable by status + supplier + date range. The detail
// drawer surfaces the line editor (in draft), the Send button (in draft),
// the Receive drawer (in sent / partial), and Cancel (draft / sent).

interface Supplier {
  id: string
  name: string
  active: number
}

interface PO {
  id: string
  po_number: string
  supplier_id: string
  supplier_name: string | null
  store_id: string
  status: 'draft' | 'sent' | 'partial' | 'received' | 'cancelled'
  expected_delivery: string | null
  subtotal_fils: number
  notes: string | null
  sent_at: string | null
  received_at: string | null
  cancelled_at: string | null
  created_at: string
}

interface POItem {
  id: string
  purchase_order_id: string
  product_id: string
  product_name: string | null
  product_sku: string | null
  ordered_qty: number
  received_qty: number
  unit_cost_fils: number
  line_total_fils: number
}

const STATUS_LABEL: Record<PO['status'], string> = {
  draft: 'Draft', sent: 'Sent', partial: 'Partial', received: 'Received', cancelled: 'Cancelled',
}
const STATUS_COLOR: Record<PO['status'], string> = {
  draft:     'bg-gray-500/15 text-gray-400',
  sent:      'bg-blue-500/15 text-blue-400',
  partial:   'bg-amber-500/15 text-amber-400',
  received:  'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400',
}

export default function PurchaseOrders() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const [filters, setFilters] = useState<{ status: string; supplier_id: string; from_date: string; to_date: string }>({
    status: '', supplier_id: '', from_date: '', to_date: '',
  })
  const [createOpen, setCreateOpen] = useState(false)
  const [openPo, setOpenPo] = useState<PO | null>(null)
  // `?open=<po_id>` from a deep link (e.g. AP invoice drawer → "Open PO")
  // pre-loads that PO into the drawer. `&receive=1` also auto-opens the
  // Receive shipment modal — one-click path from the invoice to receiving
  // stock, so ops doesn't have to navigate + find the PO + click Receive.
  const [searchParams, setSearchParams] = useSearchParams()
  const consumedOpenRef = useRef<string | null>(null)
  const [pendingReceive, setPendingReceive] = useState(false)
  useEffect(() => {
    const target = searchParams.get('open')
    if (!target || consumedOpenRef.current === target) return
    consumedOpenRef.current = target
    const wantsReceive = searchParams.get('receive') === '1'
    api.get(`/purchase-orders/${target}`)
      .then(r => {
        setOpenPo(r.data.purchase_order as PO)
        if (wantsReceive) setPendingReceive(true)
      })
      .catch(() => {/* stale link — silently ignore */})
      .finally(() => {
        // Clean the params so a page refresh doesn't re-open.
        const next = new URLSearchParams(searchParams)
        next.delete('open')
        next.delete('receive')
        setSearchParams(next, { replace: true })
      })
  }, [searchParams, setSearchParams])

  const query = useMemo(() => {
    const q = new URLSearchParams()
    if (filters.status) q.set('status', filters.status)
    if (filters.supplier_id) q.set('supplier_id', filters.supplier_id)
    if (filters.from_date) q.set('from_date', filters.from_date)
    if (filters.to_date) q.set('to_date', filters.to_date)
    return q.toString()
  }, [filters])

  const { data, isLoading } = useQuery<{ purchase_orders: PO[]; total: number }>({
    queryKey: ['purchase-orders', query],
    queryFn: () => api.get(`/purchase-orders${query ? `?${query}` : ''}`).then(r => r.data),
  })
  const { data: suppliersData } = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/suppliers').then(r => r.data),
  })
  const suppliers = suppliersData?.suppliers ?? []

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList size={20} className="text-[#c41e3a]" />
            Purchase Orders
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track supplier orders end-to-end — draft, send, receive against, and reconcile. Received stock hits <code>stock_movements</code> as <code>po_receive</code> and updates the weighted-average unit cost so margin reports stay accurate.
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
          >
            <Plus size={16} />
            New PO
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Status">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="received">Received</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
        <Field label="Supplier">
          <select value={filters.supplier_id} onChange={e => setFilters(f => ({ ...f, supplier_id: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="From">
          <input type="date" value={filters.from_date} onChange={e => setFilters(f => ({ ...f, from_date: e.target.value }))} className={inputCls} />
        </Field>
        <Field label="To">
          <input type="date" value={filters.to_date} onChange={e => setFilters(f => ({ ...f, to_date: e.target.value }))} className={inputCls} />
        </Field>
      </div>

      {/* Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.purchase_orders ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            No purchase orders match the current filters.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">PO #</th>
                <th className="text-left px-5 py-3 font-medium">Supplier</th>
                <th className="text-left px-5 py-3 font-medium">Expected</th>
                <th className="text-right px-5 py-3 font-medium">Subtotal</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-left px-5 py-3 font-medium">Created</th>
              </tr>
            </thead>
            <tbody>
              {(data?.purchase_orders ?? []).map(po => (
                <tr key={po.id}
                    className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                    onClick={() => setOpenPo(po)}>
                  <td className="px-5 py-3 text-sm text-white font-mono font-semibold">{po.po_number}</td>
                  <td className="px-5 py-3 text-sm text-gray-300">{po.supplier_name ?? <span className="text-gray-500">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-gray-400">{po.expected_delivery ?? <span className="text-gray-600">—</span>}</td>
                  <td className="px-5 py-3 text-sm text-white text-right font-mono">{formatCurrency(po.subtotal_fils)}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[po.status]}`}>
                      {STATUS_LABEL[po.status]}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-gray-500">{formatDateTime(po.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <POCreateModal
          suppliers={suppliers.filter(s => s.active === 1)}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false)
            // Fetch the fresh PO and open it in the drawer.
            api.get(`/purchase-orders/${id}`).then(r => setOpenPo(r.data.purchase_order))
          }}
        />
      )}

      {openPo && (
        <PODrawer
          po={openPo}
          canManage={canManage}
          suppliers={suppliers}
          autoOpenReceive={pendingReceive}
          onReceiveOpened={() => setPendingReceive(false)}
          onClose={() => { setOpenPo(null); setPendingReceive(false) }}
        />
      )}
    </div>
  )
}

const inputCls = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'
const selectCls = inputCls

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  )
}

// ── Create PO ─────────────────────────────────────────────────────────
function POCreateModal({
  suppliers,
  onClose,
  onCreated,
}: {
  suppliers: Supplier[]
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '')
  const [expectedDelivery, setExpectedDelivery] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!supplierId) { setError('Supplier is required'); return }
    setSubmitting(true)
    try {
      const res = await api.post('/purchase-orders', {
        supplier_id: supplierId,
        expected_delivery: expectedDelivery || null,
        notes: notes.trim() || null,
        items: [],
      })
      onCreated(res.data.id as string)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't create PO.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">New purchase order</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <Field label="Supplier">
          <select value={supplierId} onChange={e => setSupplierId(e.target.value)} className={selectCls}>
            {suppliers.length === 0 ? (
              <option value="">No active suppliers — add one first</option>
            ) : suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>

        <Field label="Expected delivery (optional)">
          <input type="date" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} className={inputCls} />
        </Field>

        <Field label="Notes (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} min-h-[60px]`}
            placeholder="Payment terms, delivery instructions, etc." />
        </Field>

        <p className="text-[11px] text-gray-600">Line items are added on the next screen.</p>
        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="submit" disabled={submitting || suppliers.length === 0} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create draft'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Detail drawer ─────────────────────────────────────────────────────
function PODrawer({
  po,
  canManage,
  suppliers,
  autoOpenReceive = false,
  onReceiveOpened,
  onClose,
}: {
  po: PO
  canManage: boolean
  suppliers: Supplier[]
  autoOpenReceive?: boolean
  onReceiveOpened?: () => void
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [receiveOpen, setReceiveOpen] = useState(false)
  const [editItemsOpen, setEditItemsOpen] = useState(false)
  const [importCsvOpen, setImportCsvOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const { data, isLoading, refetch } = useQuery<{ purchase_order: PO; items: POItem[] }>({
    queryKey: ['purchase-order', po.id],
    queryFn: () => api.get(`/purchase-orders/${po.id}`).then(r => r.data),
  })
  const detail = data?.purchase_order ?? po
  const items = data?.items ?? []

  // AP deep-link auto-open. Only fires once, only when status is
  // receivable (sent / partial); otherwise the button on the AP page
  // just opens the drawer for context.
  const autoReceiveFiredRef = useRef(false)
  useEffect(() => {
    if (!autoOpenReceive || autoReceiveFiredRef.current) return
    if (!canManage) return
    if (!['sent', 'partial'].includes(detail.status)) return
    autoReceiveFiredRef.current = true
    setReceiveOpen(true)
    onReceiveOpened?.()
  }, [autoOpenReceive, canManage, detail.status, onReceiveOpened])
  const supplierName = suppliers.find(s => s.id === detail.supplier_id)?.name ?? detail.supplier_name ?? '—'

  async function runAction(action: 'send' | 'cancel', confirmMsg: string) {
    if (!confirm(confirmMsg)) return
    setBusy(action)
    try {
      await api.post(`/purchase-orders/${po.id}/${action}`, {})
      await refetch()
      qc.invalidateQueries({ queryKey: ['purchase-orders'] })
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      alert(e.response?.data?.message ?? `Couldn't ${action}.`)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-[#0f0f0f] border-l border-white/10 overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Purchase Order</div>
              <div className="text-2xl font-mono font-bold text-white mt-1">{detail.po_number}</div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={22} /></button>
          </div>

          {/* Header card */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-3">
            <Row label="Supplier" value={supplierName} />
            <Row label="Status" value={<span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[detail.status]}`}>{STATUS_LABEL[detail.status]}</span>} />
            <Row label="Expected delivery" value={detail.expected_delivery ?? '—'} />
            <Row label="Subtotal" value={<span className="font-mono">{formatCurrency(detail.subtotal_fils)}</span>} />
            {detail.notes && <Row label="Notes" value={<span className="whitespace-pre-line">{detail.notes}</span>} />}
            <Row label="Created" value={formatDateTime(detail.created_at)} />
            {detail.sent_at && <Row label="Sent" value={formatDateTime(detail.sent_at)} />}
            {detail.received_at && <Row label="Received" value={formatDateTime(detail.received_at)} />}
            {detail.cancelled_at && <Row label="Cancelled" value={formatDateTime(detail.cancelled_at)} />}
          </div>

          {/* Items */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-sm font-semibold text-white">Line items</div>
              {canManage && detail.status === 'draft' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setImportCsvOpen(true)}
                    className="flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5"
                    title="Bulk-add lines from a CSV (e.g. a supplier's spreadsheet invoice)"
                  >
                    <Upload size={12} /> Import CSV
                  </button>
                  <button
                    onClick={() => setEditItemsOpen(true)}
                    className="text-xs px-3 py-1 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5"
                  >
                    Edit items
                  </button>
                </div>
              )}
            </div>
            {isLoading ? (
              <div className="p-6 text-center text-sm text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">
                No line items yet.{detail.status === 'draft' ? ' Click "Edit items" to add products.' : ''}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">SKU</th>
                    <th className="text-left px-4 py-2">Product</th>
                    <th className="text-right px-4 py-2">Ordered</th>
                    <th className="text-right px-4 py-2">Received</th>
                    <th className="text-right px-4 py-2">Unit cost</th>
                    <th className="text-right px-4 py-2">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const short = it.received_qty < it.ordered_qty
                    return (
                      <tr key={it.id} className="border-t border-white/10">
                        <td className="px-4 py-2 text-xs text-gray-400 font-mono">{it.product_sku ?? '—'}</td>
                        <td className="px-4 py-2 text-sm text-white">{it.product_name ?? <span className="text-gray-500">—</span>}</td>
                        <td className="px-4 py-2 text-sm text-white text-right font-mono">{it.ordered_qty}</td>
                        <td className={`px-4 py-2 text-sm text-right font-mono ${short ? 'text-amber-400' : 'text-green-400'}`}>{it.received_qty}</td>
                        <td className="px-4 py-2 text-sm text-white text-right font-mono">{formatCurrency(it.unit_cost_fils)}</td>
                        <td className="px-4 py-2 text-sm text-white text-right font-mono">{formatCurrency(it.line_total_fils)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Actions */}
          {canManage && (
            <div className="flex flex-wrap gap-2">
              {detail.status === 'draft' && (
                <>
                  <button
                    disabled={busy !== null || items.length === 0}
                    onClick={() => runAction('send', 'Mark this PO as Sent? Items become read-only.')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-300 text-sm font-semibold hover:bg-blue-500/25 disabled:opacity-40"
                  >
                    {busy === 'send' ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Send to supplier
                  </button>
                  <button
                    disabled={busy !== null}
                    onClick={() => runAction('cancel', 'Cancel this PO? This cannot be undone.')}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/25 disabled:opacity-40"
                  >
                    {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                    Cancel PO
                  </button>
                </>
              )}
              {(detail.status === 'sent' || detail.status === 'partial') && (
                <>
                  <button
                    onClick={() => setReceiveOpen(true)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
                  >
                    <PackageCheck size={14} />
                    Receive shipment
                  </button>
                  {detail.status === 'sent' && (
                    <button
                      disabled={busy !== null}
                      onClick={() => runAction('cancel', 'Cancel this PO? This cannot be undone.')}
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/25 disabled:opacity-40"
                    >
                      {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                      Cancel PO
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {editItemsOpen && (
        <ItemsEditor
          poId={detail.id}
          items={items}
          onClose={() => setEditItemsOpen(false)}
          onSaved={() => { setEditItemsOpen(false); refetch(); qc.invalidateQueries({ queryKey: ['purchase-orders'] }) }}
        />
      )}

      {importCsvOpen && (
        <ImportCsvModal
          poId={detail.id}
          existingItems={items}
          onClose={() => setImportCsvOpen(false)}
          onImported={() => { setImportCsvOpen(false); refetch(); qc.invalidateQueries({ queryKey: ['purchase-orders'] }) }}
        />
      )}

      {receiveOpen && (
        <ReceiveShipmentModal
          poId={detail.id}
          items={items}
          onClose={() => setReceiveOpen(false)}
          onReceived={() => { setReceiveOpen(false); refetch(); qc.invalidateQueries({ queryKey: ['purchase-orders'] }) }}
        />
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 mt-0.5 min-w-[110px]">{label}</div>
      <div className="text-sm text-white text-right flex-1">{value}</div>
    </div>
  )
}

// ── Items editor (draft only) ─────────────────────────────────────────
interface ProductPick { id: string; sku: string; name: string; size_ml: number | null }

// Cashier-friendly volume label — matches the till convention.
// 750ml, 1L, 0.75L, etc. Null → empty string so the caller can hide it.
function formatVolume(ml: number | null | undefined): string {
  if (!ml || ml <= 0) return ''
  if (ml >= 1000 && ml % 1000 === 0) return `${ml / 1000}L`
  if (ml >= 1000) return `${(ml / 1000).toFixed(2)}L`
  return `${ml}ml`
}

function ItemsEditor({
  poId,
  items,
  onClose,
  onSaved,
}: {
  poId: string
  items: POItem[]
  onClose: () => void
  onSaved: () => void
}) {
  const [lines, setLines] = useState<Array<{
    product_id: string
    product_name: string
    ordered_qty: string
    unit_cost_fils: string
  }>>(() => items.map(it => ({
    product_id: it.product_id,
    product_name: it.product_name ?? it.product_sku ?? it.product_id,
    ordered_qty: String(it.ordered_qty),
    unit_cost_fils: (it.unit_cost_fils / 1000).toFixed(3),
  })))
  const [search, setSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: productsData } = useQuery<{ products: ProductPick[] }>({
    queryKey: ['product-picker', search],
    queryFn: async () => {
      const r = await api.get(`/products?search=${encodeURIComponent(search)}&limit=20`)
      return { products: r.data.products as ProductPick[] }
    },
    enabled: search.length > 0,
  })

  const subtotal = useMemo(
    () => lines.reduce((a, l) => a + (parseInt(l.ordered_qty || '0', 10) * Math.round(parseFloat(l.unit_cost_fils || '0') * 1000)), 0),
    [lines]
  )

  function addProduct(p: ProductPick) {
    if (lines.some(l => l.product_id === p.id)) return
    const vol = formatVolume(p.size_ml)
    setLines(prev => [...prev, {
      product_id: p.id,
      // Include size in the display name so the operator can distinguish
      // 750ml / 1L variants in the line-item table without re-opening
      // the picker.
      product_name: vol ? `${p.name} · ${vol}` : p.name,
      ordered_qty: '1',
      unit_cost_fils: '0.000',
    }])
    setSearch('')
  }

  async function save() {
    setError(null)
    for (const l of lines) {
      const qty = parseInt(l.ordered_qty || '0', 10)
      const cost = parseFloat(l.unit_cost_fils || '0')
      if (!(qty > 0)) { setError(`Every line needs a positive Qty.`); return }
      if (!(cost > 0)) { setError(`Every line needs a positive Unit cost.`); return }
    }
    setSaving(true)
    try {
      await api.patch(`/purchase-orders/${poId}/items`, {
        items: lines.map(l => ({
          product_id: l.product_id,
          ordered_qty: parseInt(l.ordered_qty, 10),
          unit_cost_fils: Math.round(parseFloat(l.unit_cost_fils) * 1000),
        })),
      })
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't save.")
    } finally {
      setSaving(false)
    }
  }

  return (
    // stopPropagation so clicks inside this modal (search input, product
    // picks, quantity inputs, etc.) don't bubble up to the PODrawer's
    // outer overlay onClick which closes the whole drawer.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={e => e.stopPropagation()}
    >
      <div className="w-full max-w-3xl bg-[#0f0f0f] border border-white/10 rounded-xl p-6 max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">Edit PO line items</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        <div>
          <label className="text-xs text-gray-500">Add product</label>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`${inputCls} mt-1`}
            placeholder="Search by name or SKU…"
          />
          {search && productsData?.products && productsData.products.length > 0 && (
            <div className="mt-2 border border-white/10 rounded-lg max-h-40 overflow-y-auto">
              {productsData.products.map(p => {
                const vol = formatVolume(p.size_ml)
                return (
                  <button
                    key={p.id}
                    onClick={() => addProduct(p)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between gap-3"
                  >
                    <span className="text-white truncate flex items-center gap-2">
                      {p.name}
                      {vol && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide bg-white/10 text-gray-300 px-1.5 py-0.5 rounded">
                          {vol}
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-gray-500 font-mono shrink-0">{p.sku}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {lines.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-white/10 rounded-lg">
            Search for a product above to start adding lines.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-2 py-2">Product</th>
                <th className="text-right px-2 py-2 w-20">Qty</th>
                <th className="text-right px-2 py-2 w-28">Unit cost (JD)</th>
                <th className="text-right px-2 py-2 w-24">Line total</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => {
                const qty = parseInt(l.ordered_qty || '0', 10)
                const cost = Math.round(parseFloat(l.unit_cost_fils || '0') * 1000)
                const lineTotal = qty * cost
                return (
                  <tr key={l.product_id} className="border-t border-white/10">
                    <td className="px-2 py-2 text-sm text-white">{l.product_name}</td>
                    <td className="px-2 py-2">
                      <input type="number" value={l.ordered_qty}
                        onChange={e => setLines(prev => prev.map((x, i) => i === idx ? { ...x, ordered_qty: e.target.value } : x))}
                        className={`${inputCls} text-right font-mono`} />
                    </td>
                    <td className="px-2 py-2">
                      <input type="number" step="0.001" value={l.unit_cost_fils}
                        onChange={e => setLines(prev => prev.map((x, i) => i === idx ? { ...x, unit_cost_fils: e.target.value } : x))}
                        className={`${inputCls} text-right font-mono`} />
                    </td>
                    <td className="px-2 py-2 text-sm text-white text-right font-mono">{formatCurrency(lineTotal)}</td>
                    <td className="px-2 py-2 text-right">
                      <button onClick={() => setLines(prev => prev.filter((_, i) => i !== idx))} className="text-gray-500 hover:text-red-400">
                        <X size={16} />
                      </button>
                    </td>
                  </tr>
                )
              })}
              <tr className="border-t border-white/10 bg-white/5">
                <td colSpan={3} className="px-2 py-2 text-right text-sm font-semibold text-gray-300">Subtotal</td>
                <td className="px-2 py-2 text-right text-sm font-bold text-white font-mono">{formatCurrency(subtotal)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {saving ? 'Saving…' : 'Save items'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Receive shipment ─────────────────────────────────────────────────
function ReceiveShipmentModal({
  poId,
  items,
  onClose,
  onReceived,
}: {
  poId: string
  items: POItem[]
  onClose: () => void
  onReceived: () => void
}) {
  const outstanding = items.filter(i => i.received_qty < i.ordered_qty)
  const [receiving, setReceiving] = useState<Record<string, string>>(() => {
    const m: Record<string, string> = {}
    for (const it of outstanding) m[it.id] = String(it.ordered_qty - it.received_qty)
    return m
  })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const lines = useMemo(
    () => outstanding
      .map(it => ({ item: it, qty: parseInt(receiving[it.id] || '0', 10) }))
      .filter(l => l.qty > 0),
    [receiving, outstanding]
  )

  async function submit() {
    setError(null)
    setSubmitting(true)
    try {
      await api.post(`/purchase-orders/${poId}/receive`, {
        lines: lines.map(l => ({ item_id: l.item.id, receiving_qty: l.qty })),
      })
      onReceived()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't receive.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
      onClick={e => e.stopPropagation()}
    >
      <div className="w-full max-w-3xl bg-[#0f0f0f] border border-white/10 rounded-xl p-6 max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <PackageCheck size={18} className="text-[#c41e3a]" />
            Receive shipment
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        <p className="text-xs text-gray-500">
          Enter how many units arrived <strong>in this shipment</strong> per line. Short-shipments can be reconciled by receiving less than the outstanding qty — the remainder stays open until a follow-up receive or the PO is closed.
        </p>

        {outstanding.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-white/10 rounded-lg">
            Everything on this PO has already been received.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-right px-3 py-2 w-20">Outstanding</th>
                <th className="text-right px-3 py-2 w-24">Receiving now</th>
                <th className="text-right px-3 py-2 w-28">Unit cost</th>
              </tr>
            </thead>
            <tbody>
              {outstanding.map(it => {
                const outstandingQty = it.ordered_qty - it.received_qty
                return (
                  <tr key={it.id} className="border-t border-white/10">
                    <td className="px-3 py-2 text-xs text-gray-400 font-mono">{it.product_sku ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-white">{it.product_name ?? '—'}</td>
                    <td className="px-3 py-2 text-sm text-gray-300 text-right font-mono">{outstandingQty}</td>
                    <td className="px-3 py-2">
                      <input
                        type="number"
                        min={0}
                        max={outstandingQty}
                        value={receiving[it.id] ?? ''}
                        onChange={e => setReceiving(r => ({ ...r, [it.id]: e.target.value }))}
                        className={`${inputCls} text-right font-mono`}
                      />
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-300 text-right font-mono">{formatCurrency(it.unit_cost_fils)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || lines.length === 0}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Receiving…' : `Confirm receive (${lines.length} line${lines.length === 1 ? '' : 's'})`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── CSV import for PO lines ─────────────────────────────────────────────
//
// Bulk-add lines from a spreadsheet — typical use case is a supplier's
// email invoice pasted into a CSV. Header row required. SKU is the
// primary key (barcode also accepted).
//
// Semantics: import REPLACES the existing draft's line set. If an
// operator wants a merge they can Edit items after importing.

interface CsvRow {
  sku: string
  qty: number
  unit_cost_jd: number
  rowIndex: number
}

interface LookupResp {
  found: Array<{ input: string; product: { id: string; sku: string | null; barcode: string | null; name: string } }>
  not_found: string[]
}

interface ImportPreview {
  resolved: Array<{ csv: CsvRow; product_id: string; product_name: string; product_sku: string }>
  unmatched: CsvRow[]
  invalid: Array<{ rowIndex: number; error: string }>
}

function ImportCsvModal({
  poId,
  existingItems,
  onClose,
  onImported,
}: {
  poId: string
  existingItems: POItem[]
  onClose: () => void
  onImported: () => void
}) {
  const [fileName, setFileName] = useState<string | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [parsing, setParsing] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [downloadingList, setDownloadingList] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onFile(f: File) {
    setFileName(f.name)
    setError(null)
    setPreview(null)
    setParsing(true)
    try {
      const text = await f.text()
      const { rows, invalid } = parseCsv(text)
      if (rows.length === 0 && invalid.length === 0) {
        setError('No rows found. Expected header row + at least one data row.')
        return
      }
      const identifiers = Array.from(new Set(rows.map(r => r.sku)))
      const res = await api.post<LookupResp>('/products/lookup-by-sku', { identifiers })
      const byInput = new Map(res.data.found.map(f => [f.input, f]))
      const notFoundSet = new Set(res.data.not_found)
      const resolved: ImportPreview['resolved'] = []
      const unmatched: ImportPreview['unmatched'] = []
      for (const r of rows) {
        const m = byInput.get(r.sku)
        if (m) {
          resolved.push({
            csv: r,
            product_id: m.product.id,
            product_name: m.product.name,
            product_sku: m.product.sku ?? r.sku,
          })
        } else if (notFoundSet.has(r.sku)) {
          unmatched.push(r)
        } else {
          unmatched.push(r)
        }
      }
      setPreview({ resolved, unmatched, invalid })
    } catch {
      setError('Could not parse the file. Make sure it\'s a plain CSV.')
    } finally {
      setParsing(false)
    }
  }

  async function commit() {
    if (!preview) return
    setSubmitting(true)
    setError(null)
    try {
      await api.patch(`/purchase-orders/${poId}/items`, {
        items: preview.resolved.map(r => ({
          product_id: r.product_id,
          ordered_qty: r.csv.qty,
          unit_cost_fils: Math.round(r.csv.unit_cost_jd * 1000),
        })),
      })
      onImported()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't import.")
    } finally {
      setSubmitting(false)
    }
  }

  function saveCsv(name: string, csv: string) {
    // BOM keeps Excel from mangling non-ASCII product names (Arabic).
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = name
    a.click()
    URL.revokeObjectURL(url)
  }

  function downloadTemplate() {
    saveCsv(
      'po-items-template.csv',
      'sku,quantity,unit_cost_jd\nADM-ABC12345,10,5.500\nADM-XYZ98765,50,12.000\n'
    )
  }

  async function downloadProductsList() {
    setDownloadingList(true)
    setError(null)
    try {
      const res = await api.get<{ products: Array<{ sku: string | null; barcode: string | null; name: string; brand: string | null; category_name: string | null }> }>('/products/all-active')
      const header = 'sku,quantity,unit_cost_jd,name,category,brand\n'
      const body = res.data.products
        .filter(p => p.sku)
        .map(p => [
          csvCell(p.sku ?? ''),
          '',
          '',
          csvCell(p.name),
          csvCell(p.category_name ?? ''),
          csvCell(p.brand ?? ''),
        ].join(','))
        .join('\n')
      saveCsv('po-items-all-products.csv', header + body + '\n')
    } catch {
      setError("Couldn't fetch the product list.")
    } finally {
      setDownloadingList(false)
    }
  }

  const canCommit = preview !== null && preview.resolved.length > 0 && !submitting

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={e => e.stopPropagation()}>
      <div className="w-full max-w-3xl bg-[#0f0f0f] border border-white/10 rounded-xl p-6 max-h-[92vh] overflow-y-auto space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <Upload size={18} className="text-[#c41e3a]" />
            Import PO lines from CSV
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={20} /></button>
        </div>

        <div className="bg-[#1a1a1a] border border-white/10 rounded-lg p-4 space-y-3">
          <div className="text-sm text-gray-300">
            Required columns: <code className="text-white">sku</code>, <code className="text-white">quantity</code>, <code className="text-white">unit_cost_jd</code>.
            Header row required. <code>barcode</code> works as an alternative to <code>sku</code>. Extra columns (e.g. <code>name</code>) are ignored on import. Import <strong>replaces</strong> existing lines on this PO.
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]">
              <Upload size={14} />
              Choose CSV file
              <input type="file" accept=".csv,text/csv"
                     className="hidden"
                     onChange={e => e.target.files?.[0] && onFile(e.target.files[0])} />
            </label>
            <button onClick={downloadProductsList} disabled={downloadingList}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5 disabled:opacity-50"
              title="Download a CSV pre-filled with every active product's SKU + name — delete rows you don't need and fill in quantity + unit_cost_jd">
              <Download size={12} /> {downloadingList ? 'Loading…' : 'Download products list'}
            </button>
            <button onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5"
              title="Empty template with just the header row + two example rows">
              <Download size={12} /> Blank template
            </button>
            {fileName && <div className="text-xs text-gray-500">Loaded: <code className="text-gray-300">{fileName}</code></div>}
          </div>
          <div className="text-[11px] text-gray-500">
            💡 <strong>Products list</strong> is the fastest path for the opening-balance PO or when you're loading a full supplier order — start with every SKU, delete rows you're not receiving, fill in qty + cost.
          </div>
        </div>

        {parsing && <div className="p-4 text-center text-sm text-gray-500">Parsing + resolving…</div>}

        {existingItems.length > 0 && preview && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-md p-3 flex items-start gap-2 text-amber-200 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>This PO already has <strong>{existingItems.length}</strong> line{existingItems.length === 1 ? '' : 's'}. Import will replace them.</div>
          </div>
        )}

        {preview && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <PreviewStat label="Resolved" value={preview.resolved.length} tone="green" />
              <PreviewStat label="Unknown SKU" value={preview.unmatched.length} tone={preview.unmatched.length > 0 ? 'amber' : 'neutral'} />
              <PreviewStat label="Invalid rows" value={preview.invalid.length} tone={preview.invalid.length > 0 ? 'red' : 'neutral'} />
            </div>

            {preview.resolved.length > 0 && (
              <div className="bg-[#1a1a1a] border border-white/10 rounded-lg overflow-hidden">
                <div className="px-4 py-2 border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">Will be added</div>
                <div className="max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">Row</th>
                        <th className="text-left px-3 py-2">SKU</th>
                        <th className="text-left px-3 py-2">Product</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Unit cost</th>
                        <th className="text-right px-3 py-2">Line total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.resolved.map((r) => {
                        const lineTotal = r.csv.qty * Math.round(r.csv.unit_cost_jd * 1000)
                        return (
                          <tr key={r.csv.rowIndex} className="border-t border-white/10">
                            <td className="px-3 py-1.5 text-xs text-gray-500">{r.csv.rowIndex}</td>
                            <td className="px-3 py-1.5 text-xs text-gray-400 font-mono">{r.product_sku}</td>
                            <td className="px-3 py-1.5 text-sm text-white truncate max-w-[240px]">{r.product_name}</td>
                            <td className="px-3 py-1.5 text-sm text-white text-right font-mono">{r.csv.qty}</td>
                            <td className="px-3 py-1.5 text-sm text-gray-300 text-right font-mono">JD {r.csv.unit_cost_jd.toFixed(3)}</td>
                            <td className="px-3 py-1.5 text-sm text-white text-right font-mono">{formatCurrency(lineTotal)}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {preview.unmatched.length > 0 && (
              <div className="bg-[#1a1a1a] border border-amber-500/30 rounded-lg overflow-hidden">
                <div className="px-4 py-2 border-b border-amber-500/30 text-xs uppercase tracking-wide text-amber-400">Unknown SKU — skipped</div>
                <div className="max-h-40 overflow-y-auto text-xs">
                  {preview.unmatched.map((r) => (
                    <div key={r.rowIndex} className="px-4 py-1.5 border-b border-white/5 flex justify-between text-gray-400">
                      <span className="font-mono">Row {r.rowIndex}: {r.sku}</span>
                      <span>qty {r.qty}, JD {r.unit_cost_jd.toFixed(3)}</span>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 text-[11px] text-gray-500 border-t border-white/5">
                  Add these products first via the admin Products page, then re-import.
                </div>
              </div>
            )}

            {preview.invalid.length > 0 && (
              <div className="bg-[#1a1a1a] border border-red-500/30 rounded-lg overflow-hidden">
                <div className="px-4 py-2 border-b border-red-500/30 text-xs uppercase tracking-wide text-red-400">Invalid rows — skipped</div>
                <div className="max-h-40 overflow-y-auto text-xs">
                  {preview.invalid.map((v) => (
                    <div key={v.rowIndex} className="px-4 py-1.5 border-b border-white/5 text-gray-400">
                      Row {v.rowIndex}: {v.error}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button
            type="button"
            onClick={commit}
            disabled={!canCommit}
            className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Importing…' : preview ? `Import ${preview.resolved.length} line${preview.resolved.length === 1 ? '' : 's'}` : 'Import'}
          </button>
        </div>
      </div>
    </div>
  )
}

function PreviewStat({ label, value, tone }: { label: string; value: number; tone: 'green' | 'amber' | 'red' | 'neutral' }) {
  const toneClass = tone === 'green' ? 'text-green-400' : tone === 'amber' ? 'text-amber-400' : tone === 'red' ? 'text-red-400' : 'text-gray-400'
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-mono font-bold mt-1 ${toneClass}`}>{value}</div>
    </div>
  )
}

// Accepts sku or barcode as identifier; quantity and unit_cost_jd
// (aliases: qty, cost, unit_cost). Simple double-quoted fields
// supported; spreadsheet exports don't need escaped quotes.
function parseCsv(text: string): { rows: CsvRow[]; invalid: Array<{ rowIndex: number; error: string }> } {
  const rows: CsvRow[] = []
  const invalid: Array<{ rowIndex: number; error: string }> = []
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0)
  if (lines.length < 2) return { rows, invalid }

  const header = splitCsvLine(lines[0]).map(h => h.trim().toLowerCase())
  const idxSku = header.findIndex(h => h === 'sku' || h === 'barcode' || h === 'identifier')
  const idxQty = header.findIndex(h => h === 'quantity' || h === 'qty')
  const idxCost = header.findIndex(h => h === 'unit_cost_jd' || h === 'unit_cost' || h === 'cost' || h === 'unit_cost_jod')

  if (idxSku === -1 || idxQty === -1 || idxCost === -1) {
    invalid.push({ rowIndex: 1, error: `Header must include SKU (or barcode), quantity, unit_cost_jd. Found: ${header.join(', ')}` })
    return { rows, invalid }
  }

  for (let i = 1; i < lines.length; i++) {
    const rowIndex = i + 1
    const cells = splitCsvLine(lines[i])
    const skuRaw = (cells[idxSku] ?? '').trim()
    const qtyRaw = (cells[idxQty] ?? '').trim()
    const costRaw = (cells[idxCost] ?? '').trim().replace(/,/g, '')

    if (!skuRaw) { invalid.push({ rowIndex, error: 'Missing SKU' }); continue }
    const qty = parseInt(qtyRaw, 10)
    if (!Number.isFinite(qty) || qty <= 0) { invalid.push({ rowIndex, error: `Invalid quantity: ${qtyRaw}` }); continue }
    const cost = parseFloat(costRaw)
    if (!Number.isFinite(cost) || cost <= 0) { invalid.push({ rowIndex, error: `Invalid unit cost: ${costRaw}` }); continue }

    rows.push({ sku: skuRaw, qty, unit_cost_jd: cost, rowIndex })
  }
  return { rows, invalid }
}

function csvCell(v: string): string {
  const needsQuote = v.includes(',') || v.includes('"') || v.includes('\n')
  if (!needsQuote) return v
  return '"' + v.replace(/"/g, '""') + '"'
}

function splitCsvLine(line: string): string[] {
  const cells: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"') { inQuotes = !inQuotes; continue }
    if (c === ',' && !inQuotes) { cells.push(cur); cur = ''; continue }
    cur += c
  }
  cells.push(cur)
  return cells
}
